/**
 * Count GAP findings across a Discovery feature's dimensions.
 *
 * Discovery dimension `findings` mix positive "strength" entries with "gap"
 * entries; the dashboard's "Open Findings" stat and the FeatureTable "Findings"
 * column must count gaps only, or strengths inflate the number and read as open
 * findings. A bare string finding is treated as a gap (legacy shape). Non-array
 * `dimensions` (e.g. the `{}` on scoring-error rows) count as zero.
 */
export function countGapFindings(dimensions: unknown): number {
  const dims = (Array.isArray(dimensions) ? dimensions : []) as Array<{ findings?: unknown[] }>;
  let count = 0;
  for (const d of dims) {
    const findings = Array.isArray(d.findings) ? d.findings : [];
    count += findings.filter(
      (fd) => typeof fd === 'string' || (!!fd && typeof fd === 'object' && (fd as { type?: string }).type === 'gap'),
    ).length;
  }
  return count;
}

/**
 * Count dimensions that carry a recommendation across a Discovery feature.
 *
 * Drives the FeatureTable "Recommendations" column and the Discovery page's
 * "Recommendations" stat card. Mirrors FeatureDrillDown's RecommendationsPanel,
 * which lists `dims.filter(d => d.recommendation)`. A dimension counts when its
 * `recommendation` is a non-empty **string** (legacy `d-v1`) OR a structured
 * `DiscoveryRecommendation` **object** (`d-v2`). Non-array `dimensions` count as zero.
 */
export function countRecommendations(dimensions: unknown): number {
  const dims = (Array.isArray(dimensions) ? dimensions : []) as Array<{ recommendation?: unknown }>;
  return dims.filter((d) => {
    const rec = d.recommendation;
    // d-v2 structured recommendation (any non-null object) counts; legacy string
    // counts when non-empty — together this mirrors the panel's truthiness filter.
    if (rec !== null && typeof rec === 'object') return true;
    return typeof rec === 'string' && rec.trim() !== '';
  }).length;
}
